import {
	ActionListDivider,
	ActionListHeader,
	CalendarHighlightDateOnlyRange,
	DateString,
	DropOver,
	DropOverActionList,
	IconButton,
	PreviousNextSlider,
	ResponsiveMenu,
	Space,
	Toolbar
} from "client/Components";
import {
	Fragment,
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./MainMenu.module.less";
import {
	ensureNotNil,
	generateTrackingId,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	Action,
	ActionAndOthers,
	ActionGroup,
	ActionKind
} from "client_legacy/ndaw/actions";
import {
	actionComponentFactory,
	computeActionsToRender
} from "client/Components/ActionList/actionsRenderHelpers";
import { CalendarDateRange, getDayCountOfViewKind } from "client/Features/Calendar/CalendarPage";
import { TimeZone } from "client/DateAndTime/timezones";
import { DateOnly } from "client/DateAndTime/dateOnly";
import {
	addDays,
	convertToDateOnly,
	convertToDateTime
} from "client/DateAndTime/dateUtils";
import { CalendarContentViewKind, registerDefaultView } from "client/Api/Calendar/calendar";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	MediaQueryKind,
	useToggle,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { CalendarDateSelector } from "client/Features/Calendar/CalendarDateSelector";

export type MainMenuProps = {
	dateRange: CalendarDateRange;
	isMobile: boolean;
	isResponsive: boolean;
	onNewEvent(): void;
	onChange(newValue: DateOnly): void;
	onViewChange(view: CalendarContentViewKind): void;
	timezone: TimeZone;
	view: CalendarContentViewKind;
	calculateHighlightRangeForDate: (date: DateOnly) => CalendarHighlightDateOnlyRange;
	onNavigateToOwa?(): void;
};

export const MainMenu = (props: MainMenuProps): ReactElement => {
	const {
		dateRange,
		isMobile,
		isResponsive,
		onNewEvent,
		onChange,
		onViewChange,
		timezone,
		view,
		calculateHighlightRangeForDate,
		onNavigateToOwa
	} = props;

	const sliderRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const viewChangeCancellation: RefObject<AbortController | undefined> = useRef<AbortController>(undefined);
	const [isDateSelectorVisible, toggleDateSelector] = useToggle(false);

	const changeDateHandler = (
		daysToAdd: number
	): void => {
		if (dateRange.toRender.length === 0) {
			throw Error("There is no date in range");
		}

		const date: DateOnly = dateRange.toRender[0];

		const newValue: DateOnly = addDays(
			date,
			daysToAdd
		);

		onChange(newValue);
	};

	const todayHandler = (): void => {
		onChange(
			convertToDateOnly(new Date(), timezone)
		);
	};

	const viewChangeHandler = useCallback(
		async (view: CalendarContentViewKind): Promise<void> => {
			viewChangeCancellation.current
				?.abort();

			const cancellation = new AbortController();
			viewChangeCancellation.current = cancellation;

			onViewChange(view);

			try {
				await registerDefaultView(view, cancellation.signal);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error("Unexpected error:", e);
			}
		},
		[onViewChange]
	);

	const actions = useMemo(
		(): readonly Action<ViewsActionsContext>[] => getActions(viewChangeHandler),
		[viewChangeHandler]
	);

	const viewsActionsContext = useMemo(
		(): ViewsActionsContext => {
			return {
				selectedView: view
			};
		},
		[view]
	);

	const dateSelectorChangeHandler = useCallback(
		(value: DateOnly): void => {
			toggleDateSelector(false);
			onChange(value);
		},
		[onChange, toggleDateSelector]
	);

	const daysCount: number = getDayCountOfViewKind(view);

	const renderSlider = (): ReactElement => {
		return (
			<PreviousNextSlider
				ref={sliderRef}
				onPrevious={() => changeDateHandler(-daysCount)}
				onNext={() => changeDateHandler(daysCount)}
				onLabel={toggleDateSelector}
				trackingId="slider"
				size="large"
			>
				<Label
					dateRange={dateRange.toRender}
					timezone={timezone}
				/>
			</PreviousNextSlider>
		);
	};

	return (
		<>
			<Toolbar buttonSize="large">
				{!isResponsive && (
					<Toolbar.Button
						kind="primary"
						text={localize("_New")}
						icon="plus"
						trackingId="create-new"
						onClick={onNewEvent}
					/>
				)}
				<Toolbar.Button
					kind="secondary"
					text={localize("_Today")}
					onClick={todayHandler}
					trackingId="today-button"
				/>
				<Toolbar.SpaceBetween />
				<Toolbar.Custom
					renderNormalMode={renderSlider}
					renderCollapsedMode={() => { throw new Error("Does not support collapse"); }}
					responsiveBehavior="always-visible"
				/>
				<Toolbar.SpaceBetween />
				{!isMobile && (
					<Toolbar.Custom
						responsiveBehavior={undefined}
						renderNormalMode={() => (
							<ViewSelect
								actions={actions}
								context={viewsActionsContext}
							/>
						)}
						renderCollapsedMode={() => (
							<CollapsedViewSelect
								actions={actions}
								context={viewsActionsContext}
							/>
						)}
					/>
				)}
				{onNavigateToOwa && (
					<Toolbar.Button
						kind="secondary"
						text={localize("_OpenAdvanceEmail")}
						icon="share-from-square"
						onClick={onNavigateToOwa}
						trackingId="open-outlook"
					/>
				)}
			</Toolbar>
			{sliderRef.current && isDateSelectorVisible && (
				<DateSelectorDropOver
					target={sliderRef.current}
					onClose={() => toggleDateSelector(false)}
					onChange={dateSelectorChangeHandler}
					timezone={timezone}
					date={dateRange.value}
					calculateHighlightRangeForDate={calculateHighlightRangeForDate}
				/>
			)}
		</>
	);
};
MainMenu.displayName = "MainMenu";

type LabelProps = {
	dateRange: readonly DateOnly[];
	timezone: TimeZone;
};

const Label = (props: LabelProps): ReactElement => {
	const {
		dateRange,
		timezone
	} = props;

	if (dateRange.length === 0) {
		throw Error("There is no date in range");
	}

	const firstDate: Date = convertToDateTime(
		dateRange[0],
		timezone
	);

	if (dateRange.length === 1) {
		return (
			<DateString
				value={firstDate}
				format="DMY"
				timezone={timezone}
			/>
		);
	}

	const lastDate: Date = convertToDateTime(
		dateRange[dateRange.length - 1],
		timezone
	);

	return (
		<>
			<DateString
				value={firstDate}
				format="D"
				timezone={timezone}
			/>
			<Space/>
			<DateString
				value={firstDate}
				format="MMM"
				timezone={timezone}
			/>
			<Space/>-<Space/>
			<DateString
				value={lastDate}
				format="D"
				timezone={timezone}
			/>
			<Space/>
			<DateString
				value={lastDate}
				format="MMM"
				timezone={timezone}
			/>
			<Space/>,<Space/>
			<DateString
				value={lastDate}
				format="YYYY"
				timezone={timezone}
			/>
		</>
	);
};
Label.displayName = "Label";

type ViewSelectProps = {
	actions: readonly ActionAndOthers<ViewsActionsContext>[];
	context: ViewsActionsContext;
};

const ViewSelect = (props: ViewSelectProps): ReactElement => {
	const {
		actions,
		context
	} = props;

	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const closeHandler = useCallback(
		(): void => {
			setIsOpen(false);
		},
		[]
	);

	return (
		<>
			<IconButton
				ref={ref}
				icon="calendar-week"
				kind="secondary"
				onClick={() => setIsOpen(true)}
				trackingId="select-view"
				shape="round"
				isActive={isOpen}
				size="large"
				tooltip={localize("_SelectView")}
			/>

			{isOpen && (
				<DropOverActionList
					target={ensureNotNil(ref.current)}
					onClose={closeHandler}
					targetAlignment="br"
					contentAlignment="tr"
					menuTitle={localize("_SelectView")}
					trackingId="select-view__dropover"
				>
					<ViewSelectActions
						actions={actions}
						context={context}
					/>
				</DropOverActionList>
			)}
		</>
	);
};
ViewSelect.displayName = "ViewSelect";

type CollapsedViewSelectProps = {
	actions: readonly ActionAndOthers<ViewsActionsContext>[];
	context: ViewsActionsContext;
};

const CollapsedViewSelect = (props: CollapsedViewSelectProps): ReactElement => {
	const {
		actions,
		context
	} = props;

	return (
		<>
			<ActionListDivider />
			<ViewSelectActions
				actions={actions}
				context={context}
			/>
			<ActionListDivider />
		</>
	);
};
CollapsedViewSelect.displayName = "CollapsedViewSelect";

type ViewSelectActionsProps = {
	actions: readonly ActionAndOthers<ViewsActionsContext>[];
	context: ViewsActionsContext;
};

const ViewSelectActions = (props: ViewSelectActionsProps): ReactElement => {
	const {
		actions,
		context
	} = props;

	const actionsToRender: ActionAndOthers<ViewsActionsContext>[] = computeActionsToRender(
		actions,
		context
	);

	return (
		<>
			<ActionListHeader text={localize("_SelectView")} />
			{actionsToRender.map((x) => (
				<Fragment key={x.name}>
					{actionComponentFactory(x, context)}
				</Fragment>
			))}
		</>
	);
};
ViewSelectActions.displayName = "ViewSelectActions";

type DateSelectorDropOverProps = {
	target: HTMLElement;
	onClose: () => void;
	onChange: (value: DateOnly) => void;
	timezone: TimeZone;
	date: DateOnly | undefined;
	calculateHighlightRangeForDate: (date: DateOnly) => CalendarHighlightDateOnlyRange;
};

const DateSelectorDropOver = (props: DateSelectorDropOverProps): ReactElement => {
	const {
		target,
		onClose,
		onChange,
		timezone,
		date,
		calculateHighlightRangeForDate
	} = props;

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const TRACKING_ID: string = "date-selector";

	const calendarElement: ReactElement = (
		<CalendarDateSelector
			date={date}
			onChange={onChange}
			timezone={timezone}
			calculateHighlightRangeForDate={calculateHighlightRangeForDate}
			trackingId={TRACKING_ID}
		/>
	);

	return (
		<DropOver
			target={target}
			targetAlignment="bc"
			contentAlignment="tc"
			onClose={onClose}
		>
			{isResponsive
				? (
					<ResponsiveMenu
						onClose={onClose}
						trackingId={generateTrackingId(TRACKING_ID, "__dropover")}
					>
						<ResponsiveMenu.Header title={localize("_Date")} />

						<ResponsiveMenu.Body>
							{calendarElement}
						</ResponsiveMenu.Body>
					</ResponsiveMenu>
				)
				: (
					<div
						{...setTrackingId(TRACKING_ID, "__dropover")}
						className={styles.dateDropOver}
					>
						{calendarElement}
					</div>
				)
			}
		</DropOver>
	);
};
DateSelectorDropOver.displayName = "DateSelector";

function getActions(
	onViewChange: (view: CalendarContentViewKind) => void
): readonly Action<ViewsActionsContext>[] {
	return [
		{
			kind: ActionKind.SelectOption,
			name: "day",
			title: localize("_Day"),
			canExecute: () => true,
			execute: () => {
				onViewChange(CalendarContentViewKind.Day);
			},
			isChecked: (c) => c.selectedView === CalendarContentViewKind.Day,
			group: selectViewGroup
		},
		{
			kind: ActionKind.SelectOption,
			name: "work-week",
			title: localize("_WorkWeek"),
			canExecute: () => true,
			execute: () => {
				onViewChange(CalendarContentViewKind.WorkWeek);
			},
			isChecked: (c) => c.selectedView === CalendarContentViewKind.WorkWeek,
			group: selectViewGroup
		},
		{
			kind: ActionKind.SelectOption,
			name: "week",
			title: localize("_Week"),
			canExecute: () => true,
			execute: () => {
				onViewChange(CalendarContentViewKind.Week);
			},
			isChecked: (c) => c.selectedView === CalendarContentViewKind.Week,
			group: selectViewGroup
		},
		{
			kind: ActionKind.SelectOption,
			name: "3day",
			group: selectViewGroup,
			title: resolveFormattedTextString({
				template: localize("_XDays"),
				replacements: [
					{
						what: "{{Number}}",
						filler: `${getDayCountOfViewKind(CalendarContentViewKind.ThreeDays)}`
					}
				]
			}),
			canExecute: () => true,
			execute: () => {
				onViewChange(CalendarContentViewKind.ThreeDays);
			},
			isChecked: (c) => c.selectedView === CalendarContentViewKind.ThreeDays
		},
		{
			kind: ActionKind.SelectOption,
			name: "5day",
			group: selectViewGroup,
			title: resolveFormattedTextString({
				template: localize("_XDays"),
				replacements: [
					{
						what: "{{Number}}",
						filler: `${getDayCountOfViewKind(CalendarContentViewKind.FiveDays)}`
					}
				]
			}),
			canExecute: () => true,
			execute: () => {
				onViewChange(CalendarContentViewKind.FiveDays);
			},
			isChecked: (c) => c.selectedView === CalendarContentViewKind.FiveDays
		}
	];
}

const selectViewGroup: ActionGroup = {
	kind: "regular",
	name: "select-views-group",
	title: localize("_SelectView")
};

type ViewsActionsContext = {
	selectedView: CalendarContentViewKind;
};